import { Injectable } from '@angular/core';
import { OrderService } from '../order/order.service';
import { OrderDetails } from '../../shared/models/add-to-cart';
import { UserService } from '../../core/services/user.service';
import { UserProperty, CacheConstant } from '../../core/constants/app.constant';
import { Product } from '../../shared/models/product';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { AddToCartComponent } from './add-to-cart/add-to-cart.component';
import { Observable } from '../../../../node_modules/rxjs';
import { ProductsService } from './products.service';
import { InventoryAlertComponent } from './inventory-alert/inventory-alert.component';
import { ValidateProduct } from '../../shared/models/order';
import { AddProductToFavRequest, ItemNum } from '../../shared/models/product';
import { FavouritesConfirmationComponent } from '../products/favorites/favourites-confirmation/favourites-confirmation.component';
import { CreateRenameFavoritesComponent } from '../products/favorites/create-rename-favorites/create-rename-favorites.component';
import { DeleteWatchlistProductComponent } from '../products/watchlist/delete-watchlist-product/delete-watchlist-product.component';
import { AddWatchlistProductComponent } from '../products/watchlist/add-watchlist-product/add-watchlist-product.component';
import { AppUtilService } from '../../core/utils/app-util.service';
import { AppCacheService } from '../../core/services/cache/app-cache.service';
import { GoogleAnalyticsService } from '../../core/services/google-analytics.service';
import { AppConfigService } from '../../core/services/init/app-config.service';

@Injectable()
export class ProductUtilService {
  envProperties = this.cache.get(CacheConstant.Env_Properties);
  constructor(
    private cache: AppCacheService,
    private dialog: MatDialog,
    private productService: ProductsService,
    private orderService: OrderService,
    private userService: UserService,
    private gAnalytics: GoogleAnalyticsService,
    private appConfig: AppConfigService
  ) { }

  /***************Helper Methods***************/

  isProductMatch(itemId: any, product: ValidateProduct) {
    itemId = itemId.toString();
    return itemId === product.itemId.toString() ||
    itemId === (product.hin ? product.hin.toString() : '') ||
    itemId === (product.sku ? product.sku.toString() : '')
      ? true
      : false;
  }

  getInvalidCartProducts(products: Array<Product>) {
    let validProducts = true;
    let invalidProducts = [];
    let alertDialog = null;
    const excludedProducts = this.productService.getExcludedProducts(products);
    const outOfStockProducts = this.productService.getOutofStockProducts(
      products
    );

    validProducts =
      excludedProducts.length <= 0 && outOfStockProducts.length <= 0;

    if (!validProducts) {
      invalidProducts = invalidProducts
        .concat(excludedProducts)
        .concat(outOfStockProducts);

      alertDialog = this.dialog.open(InventoryAlertComponent, {
        width: '500px',
        data: {
          excludedProducts: excludedProducts,
          outOfStockProducts: outOfStockProducts
        }
      });
    }

    return { invalidProducts: invalidProducts, dialog: alertDialog };
  }

  /** Call this function to add products to cart from different sources.
   *  E.g. Search Product, Product Detail Page, Quick Order, Product Purchase History etc.
   **  Mandatory properties: itemId, cartQuantity, sku, price, companyQuantity, allocatedQuantity */
  addToCartProductDetails(products: Array<Product>) {
    return this.orderService.getOpenOrders();
  }

  addToCart(products: Array<Product>, viewContainerRef?, listName?) {
    return this.orderService.getOpenOrders().switchMap((response: any) => {
      if (!response.hasError) {
        return this.onGetOpenOrderResponse(response, products, listName).map(result => {
          return result;
        });
      }
    });
  }
  onSelectFavList(selectedFavList, itemId, prodDesc, userDetailNum, favoritesList) {
    return new Observable(observer => {
      const addProductToFavRequest = new AddProductToFavRequest();
      addProductToFavRequest.favId = selectedFavList.favId;
      addProductToFavRequest.userDetailNum = userDetailNum;
      addProductToFavRequest.isWatchList = false;
      const item = new ItemNum();
      item.itemId = itemId;
      const items = [item];
      addProductToFavRequest.items = items;
      if (selectedFavList && selectedFavList !== 'createNewList') {
        this.productService
          .addProductToFavList(addProductToFavRequest)
          .subscribe((response: any) => {
            if (response && !response.hasError) {
              this.dialog.open(FavouritesConfirmationComponent, {
                width: '500px',
                data: {
                  itemCIN: itemId,
                  productDescription: prodDesc,
                  favList: selectedFavList,
                  duplicateProducts: response.errorItemIdList,
                  event: 'added'
                }
              }).afterClosed()
                .subscribe(result => {
                  observer.next({ refreshList: false, addResponse: response });
                  observer.complete();
                });
            }
          });
      } else if (selectedFavList === 'createNewList') {
        this.dialog.open(CreateRenameFavoritesComponent, {
          width: '500px',
          data: {
            favList: favoritesList
          }
        })
          .afterClosed()
          .subscribe(result => {
            if (result) {
              addProductToFavRequest.favId = result.favId;
              this.productService
                .addProductToFavList(addProductToFavRequest)
                .subscribe((response: any) => {
                  if (response && !response.hasError) {
                    this.dialog
                      .open(FavouritesConfirmationComponent, {
                        width: '500px',
                        data: {
                          itemCIN: itemId,
                          productDescription: prodDesc,
                          favList: { favId: result.favId, name: result.name },
                          event: 'added'
                        }
                      }).afterClosed()
                      .subscribe(resultFrmConf => {
                        if (resultFrmConf) {
                          observer.next({ refreshList: true, addResponse: response });
                          observer.complete();
                        }
                      });
                  }
                });
            } else {
              observer.next({ refreshList: false, addResponse: { favId: null } });
              observer.complete();
            }
          });
      }
    });
  }

  onAddToWatchList(product) {
    const addProductToFavRequest = new AddProductToFavRequest();
    addProductToFavRequest.isWatchList = true;
    const item = new ItemNum();
    item.itemId = product.itemId;
    const items = [item];
    addProductToFavRequest.items = items;
    this.productService
      .addProdToWatchList(addProductToFavRequest)
      .subscribe((response: any) => {
        if (!response.hasError) {
          product.isWatchListItem = true;
          product.watchItemId = response.items[0].favItemId;
          this.dialog.open(AddWatchlistProductComponent, {
            width: '500px',
            data: {
              itemCIN: product ? product.itemId : '',
              productDescription: product ? product.description : ''
            }
          })
            .afterClosed()
            .subscribe(result => {
              if (result) {
                AppUtilService.router.navigate(['/product/watchlist']);
              }
            });
        }
      });
  }

  onDeleteWatchList(product, selectedPageSize) {
    return new Observable(observer => {
      this.dialog.open(DeleteWatchlistProductComponent, {
        width: '500px',
        data: {
          itemCIN: product ? product.itemId : '',
          productDescription: product ? product.description : ''
        }
      })
        .afterClosed()
        .subscribe(result => {
          if (result) {
            const defaultAccount = this.userService.getDefaultAccount();
            const request = {
              accountNumber: defaultAccount.accountShipToNum,
              buId: this.envProperties.buId,
              items: [
                {
                  favItemId: product.watchItemId
                }
              ],
              pageSize: selectedPageSize,
              shipToNum: defaultAccount.accountShipToNum,
              soldToNum: defaultAccount.accountSoldToNum,
              userDetailNum: defaultAccount.userDetailNum
            };
            this.productService
              .deleteProdFromWatchList(request)
              .subscribe((response: any) => {
                if (!response.hasError) {
                  product.isWatchListItem = false;
                  product.watchItemId = null;
                  observer.next(response);
                  observer.complete();
                }
              });
          }
        });
    });
  }

  onAddToWatchListToaster(product, itemId, description) {
    const addProductToFavRequest = new AddProductToFavRequest();
    addProductToFavRequest.isWatchList = true;
    const item = new ItemNum();
    item.itemId = itemId;
    const items = [item];
    addProductToFavRequest.items = items;
    this.productService.addProdToWatchList(addProductToFavRequest).subscribe((response: any) => {
      if (!response.hasError) {
        product.watchItemId = response.items[0].favItemId;
        const watchlistMsg = itemId + ' - ' + description + ' has been added to your Watchlist.';
        AppUtilService.message.success(watchlistMsg);
      }
    });
  }

  onDeleteFromWatchListToaster(product, itemId, description) {
    const defaultAccount = this.userService.getDefaultAccount();
    const request = {
      accountNumber: defaultAccount.accountShipToNum,
      buId: this.envProperties.buId, 
      items: [
        {
          favItemId: product.watchItemId
        }
      ],
      pageSize: null,
      shipToNum: defaultAccount.accountShipToNum,
      soldToNum: defaultAccount.accountSoldToNum,
      userDetailNum: defaultAccount.userDetailNum
    };
    this.productService.deleteProdFromWatchList(request).subscribe((response: any) => {
      if (!response.hasError) {
        const watchlistMsg = itemId + ' - ' + description + ' has been removed from your Watchlist.';
        AppUtilService.message.success(watchlistMsg);
      }
    });
  }

  /***************Helper Methods***************/

  /***************Private Methods***************/

   onGetOpenOrderResponse(response, products: Array<Product>, viewContainerRef?, listName?) {
    return new Observable(observer => {
      const orderDetails: Array<OrderDetails> = [];
      const promptForNewOrder = this.userService.getProperty(
        UserProperty.PromptForNewOrder
      );
      const defaultAccount = this.userService.getDefaultAccount();
      const orderData = [];
      products
        .filter(product => product.cartQuantity > 0)
        .forEach(product => {
          orderDetails.push({
            itemId: product.itemId,
            orderQuantity: product.cartQuantity,
            sku: product.sku,
            unitPrice: product.price,
            allocatedQuantity: product.allocatedQuantity,
            packQuantity: product.packQuantity,
            wareHouse: product.wareHouse,
            media: product.media,
            mediaName: product.mediaName,
            negotiatedReasonCode: Number(product.negotiatedReason),
            negotiatedPrice:product.negotiatedPrice,
            negotiationComments: product.negotiationComments,
            competitorName:product.competitorName,
            addedFromProductRecommended:product.addedFromProductRecommended?product.addedFromProductRecommended:'0'
            
          });

          orderData.push({
            "id": product.itemId,
            "name": product.description,
            "list_name": listName,
            "brand": product.brandName,
            "quantity": product.cartQuantity,
            "price": product.price
          })
        });

      if (
        !response.openOrders.isEmpty() &&
        (promptForNewOrder || response.openOrders.length > 1)
      ) {
        this.dialog
          .open(AddToCartComponent, {
            data: {
              orderDetails: orderDetails,
              openOrders: response.openOrders,
              gTagData: orderData
            }
          })
          .afterClosed()
          .subscribe(result => {
            if (result) {
              this.validateLowQuantityProducts(products);
            }

            observer.next(result);
            observer.complete();
          });
      } else {
        const order = !response.openOrders.isEmpty()
          ? response.openOrders[0]
          : null;

        this.orderService
          .addToCart(orderDetails, order)
          .subscribe((cartResponse: any) => {
            if (!cartResponse.hasError) {
              if (this.appConfig.gTrackingId) {
                this.gAnalytics.logAddToCart(orderData, defaultAccount.accountSoldToNum);
              }
              this.orderService.cartCountUpdate();
              if(this.envProperties.buId==1)
              {
                this.validateLowQuantityProducts(products);
              }
            }
            observer.next(cartResponse);
            observer.complete();
          });
      }
    });
  }

  private validateLowQuantityProducts(products: Array<Product>) {
    const quantityProducts = this.getQuantityProducts(products);
    const lowQuantityProducts = this.productService.getLowQuantityProducts(
      quantityProducts
    );

    if (lowQuantityProducts.length > 0) {
      this.dialog.open(InventoryAlertComponent, {
        width: '500px',
        data: {
          lowQuantityProducts: lowQuantityProducts
        }
      });
    }
  }

  private getQuantityProducts(products: Array<Product>) {
    return products.filter(product => product.cartQuantity > 0);
  }

  /***************Private Methods***************/
}
